import React from 'react';

// System / switchboard / room filters shared by the cable list and the job package form.

const idOf = (v) => (v && typeof v === 'object' ? v._id : v) || null;
const padRoom = (n) => (n != null ? String(n).padStart(3, '0') : '');
const firstWord = (s) => (s || '').trim().split(/\s+/)[0];
const roomLabel = (r) => `${padRoom(r.number)} ${r.text}`;
const emptyGroups = () => ({ system: new Set(), switchboard: new Set(), room: new Set() });

export const GROUP_FILTERS = [
  { key: 'system',      all: 'All systems' },
  { key: 'switchboard', all: 'All switchboards' },
  { key: 'room',        all: 'All rooms' },
];
export const NO_GROUP_FILTER = { system: '', switchboard: '', room: '' };

// The switchboard's own item (tag = switchboard name/tag, or a SWITCHBOARD item named after it, e.g. "L02 230V SWITCHBOARD")
const ownSwitchboard = (item, switchboards) => item && switchboards.find(sb =>
  (item.tag && (item.tag === sb.name || item.tag === sb.tag)) ||
  (/switchboard/i.test(item.itemType || '') && firstWord(item.description) === sb.name));

// Collects the names of every system, switchboard and room the items and breakers refer to
function collectNames(items, breakers) {
  const names = { system: new Map(), switchboard: new Map(), room: new Map() };
  const sbById = new Map();
  for (const i of items) {
    if (i.system?._id) names.system.set(i.system._id, i.system.name);
    if (i.room?._id) names.room.set(i.room._id, roomLabel(i.room));
    if (i.switchboard?._id) sbById.set(i.switchboard._id, i.switchboard);
  }
  for (const b of breakers) {
    const sb = b?.switchboard;
    if (sb?._id) sbById.set(sb._id, { ...sbById.get(sb._id), ...sb });
    if (sb?.room?._id) names.room.set(sb.room._id, roomLabel(sb.room));
  }
  sbById.forEach((sb, id) => names.switchboard.set(id, sb.name));
  return { names, switchboards: [...sbById.values()] };
}

// An item belongs to its system, room and feeding switchboard — and a switchboard's own item to that switchboard
function addItemGroups(g, item, switchboards) {
  if (!item) return;
  if (item.system) g.system.add(idOf(item.system));
  if (item.room) g.room.add(idOf(item.room));
  if (item.switchboard) g.switchboard.add(idOf(item.switchboard));
  const own = ownSwitchboard(item, switchboards);
  if (own) g.switchboard.add(own._id);
}

// One end of a cable. An end on a breaker belongs to the breaker's switchboard (not the switchboard feeding the item).
function addCableEndGroups(g, cable, end, itemsById, switchboards) {
  const item = itemsById.get(idOf(cable[end]));
  const brk = cable[end === 'from' ? 'fromBreaker' : 'toBreaker'];
  if (item?.system) g.system.add(idOf(item.system));
  if (item?.room) g.room.add(idOf(item.room));
  else if (brk?.switchboard?.room) g.room.add(idOf(brk.switchboard.room));
  if (brk?.switchboard) g.switchboard.add(idOf(brk.switchboard));
  else if (item?.switchboard) g.switchboard.add(idOf(item.switchboard));
  const own = ownSwitchboard(item, switchboards);
  if (own) g.switchboard.add(own._id);
}

// Dropdown options: only values some row actually uses, sorted by label
function optionsFor(rowGroups, names) {
  const used = emptyGroups();
  rowGroups.forEach(g => Object.keys(used).forEach(k => g[k].forEach(id => used[k].add(id))));
  return Object.fromEntries(Object.keys(used).map(k => [k,
    [...used[k]].filter(id => names[k].has(id))
      .map(id => ({ value: id, label: names[k].get(id) }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true })),
  ]));
}

// → { groups: Map(cableId -> {system, switchboard, room}), options }  (a cable matches if its From or To end does)
export function buildCableGroups(cables, items, breakers = []) {
  const cableBreakers = cables.flatMap(c => [c.fromBreaker, c.toBreaker]).filter(Boolean);
  const { names, switchboards } = collectNames(items, [...breakers, ...cableBreakers]);
  const itemsById = new Map(items.map(i => [i._id, i]));
  const groups = new Map(cables.map(c => {
    const g = emptyGroups();
    addCableEndGroups(g, c, 'from', itemsById, switchboards);
    addCableEndGroups(g, c, 'to', itemsById, switchboards);
    return [c._id, g];
  }));
  return { groups, options: optionsFor(groups, names) };
}

// → { groups: Map(itemId -> {system, switchboard, room}), options }
export function buildItemGroups(items) {
  const { names, switchboards } = collectNames(items, []);
  const groups = new Map(items.map(i => { const g = emptyGroups(); addItemGroups(g, i, switchboards); return [i._id, g]; }));
  return { groups, options: optionsFor(groups, names) };
}

export const matchesGroupFilter = (groups, id, filter) =>
  Object.entries(filter).every(([k, v]) => !v || groups.get(id)?.[k].has(v));

export function GroupFilterSelects({ options, value, onChange, className = 'group-filter' }) {
  return GROUP_FILTERS.map(({ key, all }) => (
    <select key={key} className={`${className}${value[key] ? ' active' : ''}`} value={value[key]}
      title={`Only rows where this ${key} is involved`}
      onChange={e => onChange({ ...value, [key]: e.target.value })}>
      <option value="">{all}</option>
      {options[key].map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  ));
}
